import {Control} from 'ol/control';
import GIS from "../app";
import {LayersOptions} from "../configuration/control";
import LayerGroup from "ol/layer/Group";
import {LayerConfiguration, LayerType, SwitcherActivation} from "../configuration/layer";
import {Emitter} from "../base/emitter";
import Cluster from "ol/source/Cluster";
import {SwitcherOptions} from "../general/options";
import {Extent, getCenter, isEmpty} from "ol/extent";
import {Coordinate} from "ol/coordinate";
import {Feature} from "ol";
import {Polygon} from "ol/geom";
import Point from "ol/geom/Point";
import VectorSource from "ol/source/Vector";
import {FilterConfig} from "../configuration/group.ts";

const defaultOptions = {
    title: 'Layer',
    tip: 'Layer switcher',
    hideTip: 'Hide panel'
}

type ResolvedFilterConfig = {
    enabled: boolean;
    options: { tipLabel: string };
};

class LayersControl extends Control {
    private readonly GIS;
    private readonly showButton;
    private readonly switcher;
    private isShown = false;
    private layersOptions: LayersOptions;
    private emitter: Emitter;

    private groupFilterInputs: Map<string, HTMLInputElement> = new Map();
    private groupTagPaletteEls: Map<string, HTMLDivElement> = new Map();
    private groupSelectedChipsEls: Map<string, HTMLDivElement> = new Map();
    private groupAllTags: Map<string, Map<string, number>> = new Map();
    private groupSelectedTags: Map<string, Set<string>> = new Map();
    private groupTagSuggestEls: Map<string, HTMLUListElement> = new Map();

    constructor(GIS: GIS, emitter: Emitter, options: LayersOptions = defaultOptions) {
        const element = document.createElement('div');

        super({
            element: element,
            target: options && options.target
        });

        this.GIS = GIS;
        this.layersOptions = options;
        this.emitter = emitter;

        this.showButton = document.createElement('button');
        this.showButton.innerHTML = '<i class="fal fa-layer-group"/>';
        this.showButton.id = `cfm-layers-button-${this.GIS.getInstanceCounter()}`;
        this.showButton.type = 'button';
        this.GIS.Localisation.bindTitle(
            this.showButton,
            options?.i18n?.tip,
            options?.tip ?? 'Layer switcher'
        );

        element.className = 'cfm-tool cfm-layers ol-unselectable ol-control';
        element.appendChild(this.showButton);

        this.switcher = document.createElement('div');

        if (options) {
            this.createSwitcher(options);
        }
        this.showButton.addEventListener('click', () => {
            this.togglePanel()
        }, false);

        this.emitter.on('layersHide', this.hidePanel.bind(this));
        this.emitter.on('LayersShow', this.showPanel.bind(this));
    }

    private normalizeForSearch(input: string | string[] | undefined): string {
        if (!input) return '';
        const s = Array.isArray(input) ? input.join(' ') : String(input);
        return s
            .toLowerCase()
            .normalize('NFD')
            .replace(/\p{Diacritic}/gu, '')
            .replace(/\s+/g, ' ')
            .trim();
    }

    private splitTags(normalized: string): string[] {
        if (!normalized) return [];
        return [...new Set(normalized.split(' ').filter(Boolean))];
    }

    private ensureGroupStores(groupName: string) {
        if (!this.groupAllTags.has(groupName)) this.groupAllTags.set(groupName, new Map());
        if (!this.groupSelectedTags.has(groupName)) this.groupSelectedTags.set(groupName, new Set());
    }

    private rebuildTagHeaders(groupName: string) {
        const host = this.getGroupDiv(groupName);
        if (!host) return;

        host.querySelectorAll('.cfm-tag-header').forEach(h => h.remove());

        const visibleLayers = Array.from(host.querySelectorAll<HTMLDivElement>('.cfm-layer'))
            .filter(el => el.style.display !== 'none');

        if (visibleLayers.length === 0) return;

        type Bucket = { tags: string[]; items: HTMLDivElement[]; firstIndex: number };
        const buckets = new Map<string, Bucket>();

        visibleLayers.forEach((el, idx) => {
            const tags = (el.dataset.tagslist || '').split(' ').filter(Boolean);
            const key = [...new Set(tags)].sort((a,b) => a.localeCompare(b)).join(' ');
            if (!buckets.has(key)) {
                buckets.set(key, { tags: key ? key.split(' ') : [], items: [el], firstIndex: idx });
            } else {
                buckets.get(key)!.items.push(el);
            }
        });

        [...buckets.values()]
            .sort((a,b) => a.firstIndex - b.firstIndex)
            .forEach(bucket => {
                const header = this.createTagHeader(bucket.tags);
                const firstEl = bucket.items[0];
                firstEl.parentElement!.insertBefore(header, firstEl);
            });
    }

    private createTagHeader(tags: string[]): HTMLDivElement {
        const header = document.createElement('div');
        header.className = 'cfm-tag-header';
        header.dataset.tagskey = tags.join(' ');
        if (tags.length === 0) {
            const chip = document.createElement('div');
            chip.className = 'cfm-chip cfm-chip-selected';
            chip.textContent = 'untagged';
            header.appendChild(chip);
            return header;
        }
        for (const t of tags) {
            const chip = document.createElement('div');
            chip.className = 'cfm-chip cfm-chip-selected';
            chip.textContent = t;
            header.appendChild(chip);
        }
        return header;
    }

    private applyFilterForGroup(groupName: string) {
        const host = this.getGroupDiv(groupName);
        const groupHeader = document.getElementById(
            `group-header-${groupName}-${this.GIS.getInstanceCounter()}`
        ) as HTMLElement | null;
        if (!host) return;

        const selectedTags = this.groupSelectedTags.get(groupName) ?? new Set<string>();
        const need = [...selectedTags];

        host.querySelectorAll<HTMLDivElement>('.cfm-layer').forEach(el => {
            const tags = (el.dataset.tags || '').split(' ').filter(Boolean);
            const show = need.length === 0 || need.some(t => tags.includes(t));
            el.style.display = show ? '' : 'none';
        });

        const hasVisibleChild = Array.from(host.querySelectorAll<HTMLDivElement>('.cfm-layer'))
            .some(child => child.style.display !== 'none');

        host.style.display = hasVisibleChild ? '' : 'none';
        if (groupHeader) groupHeader.style.display = hasVisibleChild ? '' : 'none';

        this.rebuildTagHeaders(groupName);
    }

    private renderTagSuggestions(groupName: string, query: string, limit = 20) {
        const host = this.groupTagSuggestEls.get(groupName);
        const input = this.groupFilterInputs.get(groupName);
        if (!host) return;

        const allTags = this.groupAllTags.get(groupName) ?? new Map<string, number>();
        const selected = this.groupSelectedTags.get(groupName) ?? new Set<string>();
        const searchedQuery = this.normalizeForSearch(query);

        host.innerHTML = '';

        if (!searchedQuery) {
            host.style.display = 'none';
            input?.setAttribute('aria-expanded', 'false');
            input?.removeAttribute('aria-activedescendant');
            return;
        }

        const matches = [...allTags.entries()]
            .filter(([tag]) => !selected.has(tag) && tag.includes(searchedQuery))
            .sort((a, b) => (b[1] - a[1]) || a[0].localeCompare(b[0]))
            .slice(0, limit);

        if (matches.length === 0) {
            host.style.display = 'none';
            input?.setAttribute('aria-expanded', 'false');
            input?.removeAttribute('aria-activedescendant');
            return;
        }

        matches.forEach(([tag, count], i) => {
            const li = document.createElement('li');
            li.className = 'cfm-tag-suggest-item';
            li.textContent = `${tag} (${count})`;
            li.dataset.tag = tag;
            li.id = `${host.id}-opt-${i}`;
            li.setAttribute('role', 'option');

            li.addEventListener('mousedown', (e) => {
                e.preventDefault();
                this.addSelectedTag(groupName, tag);
                if (input) {
                    input.value = ''
                }
                this.renderTagSuggestions(groupName, '');
            });
            host.appendChild(li);
        });

        host.style.display = '';
        input?.setAttribute('aria-expanded', 'true');
    }

    private renderTagPalette(groupName: string, limit = 30) {
        const palette = this.groupTagPaletteEls.get(groupName);
        if (!palette) return;
        const allTags = this.groupAllTags.get(groupName) ?? new Map();
        const selected = this.groupSelectedTags.get(groupName) ?? new Set();

        const available = [...allTags.entries()]
            .filter(([t]) => !selected.has(t))
            .sort((a, b) => (b[1] - a[1]) || a[0].localeCompare(b[0]))
            .slice(0, limit);

        palette.innerHTML = '';
        for (const [tag, count] of available) {
            const chip = document.createElement('button');
            chip.type = 'button';
            chip.className = 'cfm-chip cfm-chip-suggest';
            chip.textContent = `${tag} (${count})`;
            chip.title = `Add tag: ${tag}`;
            chip.addEventListener('click', (e) => {
                e.stopPropagation();
                this.addSelectedTag(groupName, tag);
            });
            palette.appendChild(chip);
        }
    }

    private renderSelectedChips(groupName: string) {
        const host = this.groupSelectedChipsEls.get(groupName);
        if (!host) return;
        const selected = this.groupSelectedTags.get(groupName) ?? new Set();

        host.innerHTML = '';

        if (selected.size === 0) {
            const hint = document.createElement('div');
            hint.className = 'cfm-chip-hint';
            host.appendChild(hint);
            return;
        }

        for (const tag of selected) {
            const chip = document.createElement('span');
            chip.className = 'cfm-chip cfm-chip-selected';
            chip.textContent = tag;
            const close = this.createIconButton('cfm-chip-x', 'fas fa-times', `Remove ${tag}`);
            close.addEventListener('click', (e) => {
                e.stopPropagation();
                this.removeSelectedTag(groupName, tag);
            });
            chip.appendChild(close);
            host.appendChild(chip);
        }

        const clearBtn = document.createElement('button');
        const groupObj = this.getGroupByName(groupName);
        const filterConfig = (groupObj?.get('filter') as FilterConfig) ?? {};
        const clearText = filterConfig.options?.clearAllText ?? 'Clear all';
        const clearLocalisation = filterConfig.options?.i18n?.clearAllText;
        clearBtn.type = 'button';
        clearBtn.className = 'cfm-chip-clearall';
        this.GIS.Localisation.bindText(clearBtn, clearLocalisation, clearText);
        clearBtn.addEventListener('click', () => {
            this.groupSelectedTags.set(groupName, new Set());
            this.syncFilterInputWithSelectedTags(groupName);
            this.renderSelectedChips(groupName);
            this.renderTagPalette(groupName);
            this.applyFilterForGroup(groupName);
            this.rebuildTagHeaders(groupName);
        });
        host.appendChild(clearBtn);
    }

    private getGroupDiv(groupName: string): HTMLDivElement | null {
        return document.getElementById(
            `group-switcher-div-${groupName}-${this.GIS.getInstanceCounter()}`
        ) as HTMLDivElement | null;
    }

    private addSelectedTag(groupName: string, tag: string) {
        this.ensureGroupStores(groupName);
        const set = this.groupSelectedTags.get(groupName)!;
        const t = this.normalizeForSearch(tag);
        if (!t) return;
        set.add(t);
        this.renderSelectedChips(groupName);
        this.applyFilterForGroup(groupName);
    }

    private removeSelectedTag(groupName: string, tag: string) {
        this.ensureGroupStores(groupName);
        const set = this.groupSelectedTags.get(groupName)!;
        const t = this.normalizeForSearch(tag);
        set.delete(t);
        this.renderSelectedChips(groupName);
        this.applyFilterForGroup(groupName);
    }

    private getGroupByName(name: string): LayerGroup | undefined {
        return this.GIS.Layer.groupEntries.find((group: LayerGroup) => group.get('name') === name);
    }

    private getFilterConfig(input: LayerGroup | string): ResolvedFilterConfig {
        const group = typeof input === 'string' ? this.getGroupByName(input) : input;
        const filterConfig = (group?.get('filter') as FilterConfig | undefined) ?? {};

        return {
            enabled: !!filterConfig.enabled,
            options: { tipLabel: (filterConfig.options?.tipLabel ?? 'Filter layers by tags..').trim() },
        };
    }

    private syncFilterInputWithSelectedTags(groupName: string) {
        const input = this.groupFilterInputs.get(groupName);
        if (!input) return;
        const free = this.normalizeForSearch(input.value);
        const tokens = new Set((free.match(/"[^"]+"|\S+/g) || []).map(s => s.replace(/^"|"$/g, '')));
        const selected = this.groupSelectedTags.get(groupName) ?? new Set();
        for (const t of selected) tokens.add(`tag:${t}`);
        input.value = [...tokens].join(' ');
    }

    private setVisibility = (layerName: string) => {
        const workVisible = this.GIS.Layer.getLayerVisibility(layerName, undefined);
        this.GIS.Layer.setLayerVisibility(layerName, !workVisible);

        const source = this.GIS.Layer.getLayer(layerName).getSource();
        if (source instanceof Cluster) {
            this.GIS.Layer.setLayerVisibility(source.get('name'), !workVisible);
        }
    };

    private showExtentAnimation(extent: Extent, center: Coordinate, layerName: string, duration = 2000) {
        const layer = this.GIS.Layer.getLayer(layerName);
        const vectorSource = layer?.getSource?.() as VectorSource | undefined;

        if (!layer || !vectorSource) return;

        const [minX, minY, maxX, maxY] = extent;
        const polygon = new Polygon([[
            [minX, minY],
            [minX, maxY],
            [maxX, maxY],
            [maxX, minY],
            [minX, minY],
        ]]);

        const extentFeature = new Feature(polygon);
        const centerFeature = new Feature(new Point(center));

        vectorSource.addFeatures([extentFeature, centerFeature]);

        setTimeout(() => {
            this.GIS.Layer.removeFeatures(layerName);
        }, duration);
    }

    private zoomToLayer = (
        layerName: string
    ) => {
        const extent = this.GIS.Layer.getLayerEffectiveExtent(layerName, () => {
        });
        const center = getCenter(extent);

        const zoomOptions = this.layersOptions?.zoom;
        const padding = zoomOptions?.padding ?? [50, 50, 50, 50];
        const duration = zoomOptions?.duration ?? 1000;
        const boundingBoxLayer = zoomOptions?.boundingBox?.layer;
        const boundingBoxDuration = zoomOptions?.boundingBox?.duration ?? 1000;

        if (boundingBoxLayer) {
            setTimeout(() => {
                this.showExtentAnimation(extent, center, boundingBoxLayer, boundingBoxDuration);
            }, duration);
        }

        this.GIS.View.zoomToExtent(extent, undefined, {
            padding,
            duration,
        });
    };

    /**
     * Toggle visibility of a layer entry in the switcher panel.
     * @param layerId
     * @param visible
     */
    public toggleLayerEntryVisibility(layerId: string, visible: boolean) {
        const layerElement = document.getElementById(layerId);
        if (layerElement) {
            if (visible) {
                layerElement.style.display = "";
            } else {
                layerElement.style.display = "none";
            }
        } else {
            console.warn(`Layer entry with id "${layerId}" not found.`);
        }
    };

    private hidePanel() {
        this.switcher.className = 'cfm-panel hidden';
        this.emitter.emit('sidebarHide');
    }

    private togglePanel() {
        this.isShown ? this.hidePanel() : this.showPanel();
        this.isShown = !this.isShown;
    }

    private showPanel() {
        this.isShown = true;
        this.switcher.className = 'cfm-panel visible';
        this.emitter.emit('legendHide');
        this.emitter.emit('sidebarShow');
    }

    private createIconButton(className: string, iconClass: string, title = '', id = '') {
        const button = document.createElement('button');
        button.className = className;
        button.innerHTML = `<i class="${iconClass}"></i>`;
        button.title = title;
        button.type = 'button';
        if (id) button.id = id;
        return button;
    }

    private toggleGroupVisibility(groupDiv: HTMLElement, toggleBtn: HTMLButtonElement) {
        const isVisible = groupDiv.classList.toggle('cfm-visible');
        toggleBtn.innerHTML = `<i class="${isVisible ? 'fal fa-angle-right' : 'fal fa-angle-down'}"></i>`;
    }

    private createTransparencySlider(initialValue: number, onInput: (value: number) => void): HTMLDivElement {
        const container = document.createElement('div');
        container.className = 'cfm-slidercontainer';

        const slider = document.createElement('input');
        slider.className = 'cfm-slider';
        slider.type = 'range';
        slider.min = '0';
        slider.max = '100';
        slider.value = String(initialValue);
        slider.name = 'layer-opacity';
        slider.setAttribute('aria-label', 'Layer opacity');

        slider.addEventListener('input', () => onInput(Number(slider.value)));

        container.appendChild(slider);
        return container;
    }

    private createGroup(group: LayerGroup) {
        const groupName = group.get('name');
        const groupTitleText = group.get('title') || '';
        const collapsible = group.get('collapsible') !== false;
        const groupLayer = group.get("groupLayer");
        const allLayersVisibilityCheck = group.get("allLayersVisibilityCheck");

        const groupHeader = document.createElement('span');
        groupHeader.id = `group-header-${groupName}-${this.GIS.getInstanceCounter()}`;
        groupHeader.className = 'cfm-group-header';

        const groupDiv = document.createElement('div');
        groupDiv.id = `group-switcher-div-${groupName}-${this.GIS.getInstanceCounter()}`;
        groupDiv.className = 'cfm-group-layer';

        if (collapsible) {
            const toggleBtn = this.createIconButton('cfm-group-close', 'fal fa-angle-down');
            const titleSpan = document.createElement('span');
            titleSpan.className = 'cfm-group-title';
            this.GIS.Localisation.bindLazy(
                t => { titleSpan.innerText = t; },
                () => (group.get('i18n') as { title?: Record<string, string> } | undefined)?.title,
                groupTitleText
            );

            groupHeader.appendChild(toggleBtn);
            groupHeader.appendChild(titleSpan);

            toggleBtn.addEventListener('click', () => this.toggleGroupVisibility(groupDiv, toggleBtn));
            titleSpan.addEventListener('click', () => this.toggleGroupVisibility(groupDiv, toggleBtn));
        } else {
            groupDiv.classList.add("cfm-visible");
        }

        if (groupLayer !== undefined || allLayersVisibilityCheck === true) {
            const selectAllBtn = this.createIconButton('cfm-group-check-all', 'fal fa-square');

            selectAllBtn.addEventListener('click', () => {
                const isChecked = selectAllBtn.classList.toggle("checked");
                selectAllBtn.innerHTML = `<i class="fal fa-${isChecked ? 'check-square' : 'square'}"></i>`;

                if (groupLayer) {
                    this.setVisibility(groupLayer);

                    document.querySelectorAll(`.grouped.${groupName}`).forEach(el =>
                        (el as HTMLElement).style.display = isChecked ? "" : "none"
                    );
                    document.querySelectorAll(`.singleLayer.${groupName}`).forEach(el =>
                        (el as HTMLElement).style.display = isChecked ? "none" : ""
                    );
                } else if (allLayersVisibilityCheck === true) {
                    this.GIS.Layer.setGroupVisibility(groupName, isChecked);
                }
            });

            groupHeader.appendChild(selectAllBtn);
        }

        const { enabled } = this.getFilterConfig(group);
        return { groupDiv, groupHeader, filterEnabled: enabled };
    }

    private buildGroupFilterUI(groupName: string): HTMLDivElement | null {
        const host = this.getGroupDiv(groupName);
        if (!host) return null;

        const existing = host.querySelector<HTMLDivElement>('.cfm-filterbar.group');
        if (existing) return existing;

        const rawFilterConfig = (this.getGroupByName(groupName)?.get('filter') as FilterConfig | undefined) ?? {};
        const tipLabel = (rawFilterConfig.options?.tipLabel ?? 'Filter layers by tags..').trim();
        const tipLabelLocalisation = rawFilterConfig.options?.i18n?.tipLabel;

        const groupFilterContainer = document.createElement('div');
        groupFilterContainer.className = 'cfm-filterbar group';

        const base = `cfm-tag-filter-${this.GIS.getInstanceCounter()}-${groupName.replace(/\W+/g, '_')}`;
        const listId = `${base}-listbox`;

        const label = document.createElement('label');
        label.className = 'cfm-visually-hidden';
        label.htmlFor = base;
        groupFilterContainer.appendChild(label);

        const input = document.createElement('input');
        input.type = 'search';
        input.className = 'cfm-filter-input';
        this.GIS.Localisation.bind(
            t => { input.placeholder = t; },
            tipLabelLocalisation,
            tipLabel
        );
        input.id = base;
        input.name = `tag-filter-${groupName}`;
        input.autocomplete = 'off';
        input.spellcheck = false;

        input.setAttribute('role', 'combobox');
        input.setAttribute('aria-autocomplete', 'list');
        input.setAttribute('aria-expanded', 'false');
        input.setAttribute('aria-controls', listId);

        this.groupFilterInputs.set(groupName, input);

        const suggest = document.createElement('ul');
        suggest.className = 'cfm-tag-suggest';
        suggest.id = listId;
        suggest.style.display = 'none';
        suggest.setAttribute('role', 'listbox');
        this.groupTagSuggestEls.set(groupName, suggest);

        const selectedChipsEl = document.createElement('div');
        selectedChipsEl.className = 'cfm-selected-tags';
        this.groupSelectedChipsEls.set(groupName, selectedChipsEl);

        let debounceTimer: number | undefined;
        input.addEventListener('input', () => {
            window.clearTimeout(debounceTimer);
            debounceTimer = window.setTimeout(() => {
                this.renderTagSuggestions(groupName, input.value);
            }, 100);
        });
        input.addEventListener('focus', () => {
            this.renderTagSuggestions(groupName, input.value);
        });
        input.addEventListener('keydown', (e) => {
            const items = Array.from(suggest.querySelectorAll<HTMLLIElement>('.cfm-tag-suggest-item'));
            const active = suggest.querySelector<HTMLLIElement>('.active');
            const idx = active ? items.indexOf(active) : -1;

            const setActive = (el?: HTMLLIElement) => {
                items.forEach(i => i.classList.remove('active'));
                if (el) {
                    el.classList.add('active');
                    input.setAttribute('aria-activedescendant', el.id);
                } else {
                    input.removeAttribute('aria-activedescendant');
                }
            };

            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setActive(items[Math.min(idx + 1, items.length - 1)]);
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setActive(items[Math.max(idx - 1, 0)]);
            } else if (e.key === 'Enter') {
                if (active) {
                    e.preventDefault();
                    const tag = active.dataset.tag!;
                    this.addSelectedTag(groupName, tag);
                    input.value = '';
                    this.renderTagSuggestions(groupName, '');
                }
            } else if (e.key === 'Escape') {
                suggest.style.display = 'none';
                input.setAttribute('aria-expanded', 'false');
                input.removeAttribute('aria-activedescendant');
            }
        });

        document.addEventListener('click', (ev) => {
            const t = ev.target as Node;
            if (!groupFilterContainer.contains(t)) {
                suggest.style.display = 'none';
                input.setAttribute('aria-expanded', 'false');
                input.removeAttribute('aria-activedescendant');
            }
        });

        groupFilterContainer.appendChild(input);
        groupFilterContainer.appendChild(suggest);
        groupFilterContainer.appendChild(selectedChipsEl);

        host.prepend(groupFilterContainer);

        this.renderSelectedChips(groupName);
        this.applyFilterForGroup(groupName);

        return groupFilterContainer;
    }

    private showOrHideGroupFilter(groupName: string): void {
        const host = this.getGroupDiv(groupName);
        if (!host) return;

        const hasEntries = host.querySelectorAll('.cfm-layer').length > 0;
        const { enabled } = this.getFilterConfig(groupName);

        if (enabled && hasEntries) {
            let ui = host.querySelector<HTMLDivElement>('.cfm-filterbar.group');
            if (!ui) ui = this.buildGroupFilterUI(groupName);
            if (ui) ui.style.display = '';
        } else {
            const ui = host.querySelector<HTMLDivElement>('.cfm-filterbar.group');
            if (ui) ui.style.display = 'none';
        }
    }

    private createZoomLayerIcon(zoomToLayerExtent: HTMLElement | null, layerName: string, title: HTMLDivElement) {
        if (!zoomToLayerExtent) {
            const zoomOptions = this.layersOptions?.zoom;
            zoomToLayerExtent = document.createElement('i');
            zoomToLayerExtent.className = 'fas fa-location-crosshairs';
            zoomToLayerExtent.title = zoomOptions?.title ?? 'Zoom to layer';
            zoomToLayerExtent.addEventListener('click', (event) => {
                event.stopPropagation();
                this.zoomToLayer(layerName);
            });
            title.insertBefore(zoomToLayerExtent, title.firstChild);
        }
    }

    private removeZoomLayerIcon(layerName: string) {
        const layerElement = document.getElementById(`${layerName}-${this.GIS.getInstanceCounter()}`);
        if (!layerElement) {
            console.warn(`Cannot remove zoom icon: layer element for "${layerName}" not found.`);
            return;
        }

        const titleElement = layerElement.querySelector('.cfm-caption');
        if (!titleElement) {
            console.warn(`Cannot remove zoom icon: title element for "${layerName}" not found.`);
            return;
        }

        const zoomIcon = titleElement.querySelector('.fas.fa-magnifying-glass');
        if (zoomIcon) {
            zoomIcon.remove();
        }
    }

    private createLayer(
        layerName: string,
        base: string,
        options: SwitcherOptions
    ) {
        const layer = this.GIS.Layer.getLayer(layerName);
        const source = layer.getSource();
        const container = document.createElement('div');
        container.className = 'cfm-layer';

        const image = document.createElement('img');
        if (location.protocol === 'https:') {
            options.icon = options.icon.replace(/^http:/, 'https:');
        }
        image.src = options.icon;
        container.appendChild(image);

        let baseLayer: HTMLDivElement | undefined;
        if (base) {
            baseLayer = document.createElement('div');
            baseLayer.className = 'cfm-baselayer';
            container.appendChild(baseLayer);
        }

        let sliderInput: HTMLInputElement | undefined;
        // Virtual sub-layers of a server-side composite (`wmsTiledGroup`) cannot
        // be dimmed individually on the client — their opacity is baked into the
        // combined tile server-side — so the per-layer slider is never shown for
        // them, regardless of the switcher config.
        const isCombinedGroupEntry = layer.get('wmsTiledGroupEntry') === true;
        if (options.transparencySliderVisible !== false && !isCombinedGroupEntry) {
            const currentOpacity = this.GIS.Layer.getLayerOpacity(layerName, undefined);
            const sliderWrapper = this.createTransparencySlider(currentOpacity * 100, (value: number) => {
                this.GIS.Layer.setLayerOpacity(layerName, value / 100);

                if (source instanceof Cluster) {
                    this.GIS.Layer.setLayerOpacity(source.get('name'), value / 100);
                }
            });

            sliderInput = sliderWrapper.querySelector('input')!;
            container.appendChild(sliderWrapper);
        }

        const title = document.createElement('div');
        title.className = 'cfm-caption';

        let zoomToLayerExtent: HTMLElement | null = null;

        const zoomExtent = layer.get('zoomExtent') ||
            (source && typeof source.getExtent === 'function' ? source.getExtent() : undefined);

        if (zoomExtent && !isEmpty(zoomExtent)) {
            this.createZoomLayerIcon(zoomToLayerExtent, layerName, title);
        } else {
            const source = layer.getSource?.();
            if (source && 'on' in source) {
                if ('once' in source) {
                    const updateVectorZoomIcon = () => {
                        const features = source.getFeatures?.();
                        if (features && features.length > 0) {
                            this.createZoomLayerIcon(zoomToLayerExtent, layerName, title);
                        }
                    };

                    source.once('addfeature', updateVectorZoomIcon);

                    source.on('removefeature', () => {
                        const features = source.getFeatures?.();
                        if (!features || features.length === 0) {
                            this.removeZoomLayerIcon?.(layerName);
                        }
                    });
                } else {
                    source.on('tileloadend', () => {
                        const tileGridExtent = source.getTileGrid?.()?.getExtent?.();
                        if (tileGridExtent && !isEmpty(tileGridExtent)) {
                            this.createZoomLayerIcon(zoomToLayerExtent, layerName, title);
                        }
                    });
                }
            }
        }

        const titleCaption = document.createElement('span');
        titleCaption.className = 'cfm-caption-title';
        this.GIS.Localisation.bindText(titleCaption, options.i18n?.title, options.title);
        title.appendChild(titleCaption);

        container.appendChild(title);

        return {
            switcher: container,
            image,
            baseLayer,
            slider: sliderInput,
            title,
            zoomToLayerExtent: zoomToLayerExtent as HTMLElement | null
        };
    }

    private createLayerEntry(
        groupName: string,
        groupLayerName: string | undefined,
        layer: LayerType,
        config: SwitcherOptions
    ): HTMLElement {
        const base = layer.get('base');
        const layerName = layer.get('name');
        const switcher = layer.get('switcher');
        const tags = switcher.tags || '';

        const htmlLayer = this.createLayer(layerName, base, config);

        const isGroupLayer = layerName === groupLayerName;
        const visible = this.GIS.Layer.getLayerVisibility(layerName, undefined);
        const opacity = this.GIS.Layer.getLayerOpacity(layerName, undefined);

        const classVisible = `cfm-layer cfm-layer-visible ${isGroupLayer ? 'grouped' : 'singleLayer'} ${groupName}`;
        const classHidden = `cfm-layer cfm-layer-hidden ${isGroupLayer ? 'grouped' : 'singleLayer'} ${groupName}`;

        htmlLayer.switcher.className = visible ? classVisible : classHidden;
        htmlLayer.switcher.id = `${layerName}-${this.GIS.getInstanceCounter()}`;

        const nameForSearch = this.normalizeForSearch(config.title || layerName);
        const tagsForSearch = this.normalizeForSearch(tags);
        htmlLayer.switcher.dataset.name = nameForSearch;
        htmlLayer.switcher.dataset.tags = tagsForSearch;
        htmlLayer.switcher.dataset.tagslist = this.splitTags(tagsForSearch).join(' ');

        this.ensureGroupStores(groupName);
        const tagMap = this.groupAllTags.get(groupName)!;
        for (const t of this.splitTags(tagsForSearch)) {
            tagMap.set(t, (tagMap.get(t) || 0) + 1);
        }

        if (htmlLayer.slider) {
            htmlLayer.slider.value = String(opacity * 100);
            htmlLayer.slider.oninput = () => {
                const val = Number(htmlLayer.slider!.value) / 100;
                this.GIS.Layer.setLayerOpacity(layerName, val);
            };
        }

        if (base && htmlLayer.baseLayer) {
            htmlLayer.baseLayer.innerHTML = visible
                ? '<i class="fal fa-eye"/>'
                : '<i class="fal fa-eye-slash"/>';
        }

        const deactivate = () => {
            htmlLayer.switcher.className = classHidden;
            if (htmlLayer.baseLayer) {
                htmlLayer.baseLayer.innerHTML = '<i class="fal fa-eye-slash"/>';
            }
        };

        const activate = () => {
            htmlLayer.switcher.className = classVisible;
            if (htmlLayer.baseLayer) {
                htmlLayer.baseLayer.innerHTML = '<i class="fal fa-eye"/>';
            }
        };

        (layer as LayerType & SwitcherActivation).switcher = true;
        (layer as LayerType & SwitcherActivation).deactivateSwitcher = deactivate;
        (layer as LayerType & SwitcherActivation).activateSwitcher = activate;

        const toggle = () => this.setVisibility(layerName);
        const zoom = (event: MouseEvent) => {
            event.stopPropagation();
            this.zoomToLayer(layerName);
        }

        htmlLayer.image.addEventListener('click', toggle);
        htmlLayer.title.addEventListener('click', toggle);
        if (htmlLayer.zoomToLayerExtent) {
            htmlLayer.zoomToLayerExtent.addEventListener('click', zoom);
        }
        htmlLayer.baseLayer?.addEventListener('click', toggle);

        return htmlLayer.switcher;
    }

    private createSwitcher(options: LayersOptions) {
        this.switcher.id = `cfm-switcher-${this.GIS.getInstanceCounter()}`;
        this.switcher.className = 'cfm-panel hidden';

        const switcherHeader = document.createElement('div');
        switcherHeader.className = 'cfm-panel-header';
        const switcherTitle = document.createElement('h1');
        this.GIS.Localisation.bindText(
            switcherTitle,
            options?.i18n?.title,
            options?.title ?? 'Layers'
        );
        const switcherIcon = document.createElement('i')
        switcherIcon.innerHTML = '<i class="fal fa-layer-group"/>';

        const closeButton = document.createElement('button');
        closeButton.innerHTML = '<i class="fal fa-times"/>';
        closeButton.id = `cfm-switcher-close-${this.GIS.getInstanceCounter()}`;
        closeButton.className = 'cfm-panel-close';
        closeButton.type = 'button';
        this.GIS.Localisation.bindTitle(
            closeButton,
            options?.i18n?.hideTip,
            options?.hideTip ?? 'Hide panel'
        );

        switcherHeader.appendChild(switcherIcon);
        switcherHeader.appendChild(switcherTitle);
        switcherHeader.appendChild(closeButton);
        this.switcher.appendChild(switcherHeader);

        const switcherContent = document.createElement('div');
        switcherContent.setAttribute('class', 'cfm-panel-content');

        this.GIS.Layer.groupEntries.map(group => {
            const groupTitle = group.get("title");
            const groupName = group.get("name");

            if (groupTitle) {
                const {groupHeader, groupDiv} = this.createGroup(group);
                switcherContent.appendChild(groupHeader);
                switcherContent.appendChild(groupDiv);

                group.getLayers().forEach(layer => {
                    const config = layer.get('switcher');
                    if (config) {
                        const entry = this.createLayerEntry(
                            groupName,
                            group.get('groupLayer'),
                            layer as LayerType,
                            config
                        );
                        groupDiv.appendChild(entry);
                    }
                });

                this.showOrHideGroupFilter(groupName);

                this.applyFilterForGroup(groupName);
                this.rebuildTagHeaders(groupName);
            }
        });
        this.switcher.appendChild(switcherContent);

        const targetElement = this.GIS.Map.olObject!.getTargetElement();
        targetElement.appendChild(this.switcher);

        closeButton.addEventListener('click', () => this.hidePanel(), false);
        const groupedLayersInit = document.querySelectorAll('.grouped');
        groupedLayersInit.forEach(groupedLayerInit => {
            (groupedLayerInit as HTMLElement).style.display = "none";
        });
    }

    private getGroupLayerNameFromGroupEntries(groupName: string): string | undefined {
        const group = this.GIS.Layer.groupEntries.find(
            (g: LayerGroup) => g.get('name') === groupName
        );
        return group?.get('groupLayer');
    }

    /**
     * Add a layer to the switcher panel under the specified group.
     * @param groupName
     * @param config
     */
    public addLayerToSwitcher(groupName: string, config: LayerConfiguration): void {
        const groupDiv = document.getElementById(
            `group-switcher-div-${groupName}-${this.GIS.getInstanceCounter()}`
        ) as HTMLDivElement | null;

        if (!groupDiv) {
            console.warn(`Group '${groupName}' not found in switcher.`);
            return;
        }

        const layer = this.GIS.Layer.getLayer(config.name);
        const groupLayerName = this.getGroupLayerNameFromGroupEntries(groupName);
        const entry = this.createLayerEntry(groupName, groupLayerName, layer, config.switcher as SwitcherOptions);
        groupDiv.appendChild(entry);

        this.showOrHideGroupFilter(groupName);

        if (this.groupFilterInputs.has(groupName)) {
            this.renderTagPalette(groupName);
            this.renderSelectedChips(groupName);
            this.applyFilterForGroup(groupName);
            this.rebuildTagHeaders(groupName);
        }
    }

    /**
     * Remove a layer from the switcher panel.
     * @param layerName - Name of the layer to remove.
     * @param instanceId - Instance ID of the GIS instance.
     */
    public removeLayerFromSwitcher(layerName: string, instanceId: number): void {
        const layerEntry = document.getElementById(`${layerName}-${instanceId}`);
        if (layerEntry) {
            const groupClasses = [...layerEntry.classList].filter(c => !['cfm-layer', 'cfm-layer-visible', 'cfm-layer-hidden', 'grouped', 'singleLayer'].includes(c));
            const groupName = groupClasses[0];

            if (groupName && this.groupAllTags.has(groupName)) {
                const tags = (layerEntry.dataset.tagslist || '').split(' ').filter(Boolean);
                const map = this.groupAllTags.get(groupName)!;
                for (const t of tags) {
                    const cur = map.get(t) || 0;
                    if (cur <= 1) map.delete(t); else map.set(t, cur - 1);
                }
                if (this.groupTagPaletteEls.has(groupName)) this.renderTagPalette(groupName);
            }

            layerEntry.remove();

            this.applyFilterForGroup(groupName);
            this.rebuildTagHeaders(groupName);


            this.showOrHideGroupFilter(groupName);
        } else {
            console.warn(`Layer switcher entry '${layerName}' not found in this instance with number '${instanceId}'.`);
        }
    }
}
export default LayersControl;
